import React, { ReactNode, useEffect } from 'react';
import {
  CreatePersonRequest,
  Person,
  TeamPermission,
  TeamRole, useContactLinkService,
  usePeopleService, useSyncContactService
} from 'nekst-api';
import { AccessChecker, useAuthorizationChecker } from 'authorization-scope';
import {
  AfterSubmitFunc, CheckboxInput,
  FormBlock,
  FormRows,
  IfFormData, ifReactNative,
  InputLayoutIcon,
  isReactNative,
  RelativeRow,
  Row6x6x,
  TextArea,
  TextInput,
  useFormContextRequired,
  usePopupManager,
  Wysiwyg
} from 'ui-builder';
import {
  PhoneNumbersInput,
  Popup,
  PopupText,
  ProjectRoleDropdown,
  Separator
} from 'features/nekst-widgets';
import { BasePersonFormTemplate } from './Shared/BasePersonFormTemplate';
import { AddressInput, ServiceProvidersInput, TeamRoleRadio } from '../Shared';
import { ProjectsMultiDropdown } from '../../Shared';
import useFeatureToggleService, { FubIntegrationToggleWrapper } from 'feature-toggle';
import SearchContactTextInput
  from '../../Projects/TransactionParties/AddPersonToTransactionForm/SearchContactTextInput';
import { FubConnectionContext, useFubConnectionContext } from '../../Integrations';
import { ExternalContactLinkWidget } from '../../Projects';
import { useLocation } from 'react-router-dom';

function FormRow(
  props: {
    children: ReactNode,
  }
) {
  if (isReactNative()) {
    return props.children;
  } else {
    return (
      <Row6x6x>
        {props.children}
      </Row6x6x>
    );
  }
}

export type FormType = CreatePersonRequest & {
  _createExternalContact?: boolean,
}

function CreateExternalContactCheckbox() {
  const fubContext = useFubConnectionContext();

  const formContext = useFormContextRequired<FormType>();

  const data = formContext.data;

  if (fubContext?.profiles?.length && !data?.externalLinks?.length) {
    return (
      <CheckboxInput
        source="_createExternalContact"
        label="Create Follow Up Boss Contact Record"
        hint="Once checked, this contact will be created also in Follow Up Boss"
      />
    );
  } else {
    return null;
  }
}

function ExternalContactWasNotCreatedPopup() {
  return (
    <Popup
      title="FUB Contact Creation Failed"
      content={(
        <PopupText>
          The FUB contact was not created or linked because it already exists in Follow Up Boss and is linked to another contact in your account.
        </PopupText>
      )}
    />
  );
}

function NameDoesNotMatchPopup() {
  return (
    <Popup
      title="FUB Contact Name Mismatch"
      content={(
        <PopupText>
          The name of the FUB contact doesn't match the Nekst contact. Please verify that the linked contact is correct.
        </PopupText>
      )}
    />
  );
}

enum PopupType {
  ERROR = 'ERROR',
  NAME_DOES_NOT_MATCH = 'NAME_DOES_NOT_MATCH',
}

export function useExternalContactCreationHandler() {

  const location = useLocation();

  const popupManager = usePopupManager();

  const getState = async (result: Person & {
    _externalContactMatches?: boolean,
    _externalContactCreationError?: string,
  }) => {

    let state = undefined;
    if (result._externalContactCreationError) {
      state = {
        action: 'OpenPopup',
        type: PopupType.ERROR,
      };
    } else if (result._externalContactMatches !== undefined && !result._externalContactMatches) {
      state = {
        action: 'OpenPopup',
        type: PopupType.NAME_DOES_NOT_MATCH,
      };
    }

    return state;
  };

  const handleState = async () => {

    const state = location.state;

    if (state?.action === 'OpenPopup') {

      switch (state?.type) {
        case 'ERROR':
          popupManager.openPopup(<ExternalContactWasNotCreatedPopup />);
          break;
        case 'NAME_DOES_NOT_MATCH':
          popupManager.openPopup(<NameDoesNotMatchPopup />);
          break;
      }
    }
  };

  useEffect(() => {
    handleState();
  }, [location.pathname]);

  return {
    getState,
    handleState,
  };
}

function CreatePersonFormImpl(props: {
  afterSubmitFunc?: AfterSubmitFunc<Person>,
  initialData?: CreatePersonRequest,
}) {
  const peopleService = usePeopleService();
  const { isGranted } = useAuthorizationChecker();

  const featureToggleService = useFeatureToggleService();

  const isTeamMembersManagementEnabled = featureToggleService.isTeamMembersManagementEnabled();

  const createContactService = useSyncContactService();

  const contactLinkService = useContactLinkService();

  const fubContext = useFubConnectionContext();

  return (
    <BasePersonFormTemplate<FormType, Person & {
      _externalContactMatches?: boolean,
      _externalContactCreationError?: string,
    }>
      title="Add Contact"
      submitFormFunc={async (data) => {
        const sentRequest = {
          ...data,
        } as CreatePersonRequest;

        if (data.teamRole === TeamRole.CONTACT) {
          // @ts-ignore
          sentRequest.teamRole = undefined;
        }

        const result = await peopleService.create(sentRequest);

        const profileId = fubContext?.profiles[0]?.id;

        let externalContactCreationFailed: boolean | undefined = undefined;
        let externalContactMatches: boolean | undefined = undefined;

        if (!data.externalLinks?.length && data._createExternalContact && profileId) {
          try {
            const creationResult = await createContactService.createExternalContact(
              result.id,
              profileId,
            );

            externalContactMatches = creationResult.isMatching;
            externalContactCreationFailed = false;

            await contactLinkService.updateExternalContact(result.id, result.externalLinks[0].id);
          } catch (e) {
            externalContactCreationFailed = true;
          }
        }

        return {
          ...result,
          _externalContactCreationError: externalContactCreationFailed ? 'error' : undefined,
          _externalContactMatches: externalContactMatches,
        };
      }}
      afterSubmitFunc={props.afterSubmitFunc}
      validationSchema={(data) => peopleService.getCreateValidationSchema(data.teamRole)}
      initialData={{
        teamRole: TeamRole.CONTACT,
        ...props.initialData || {},
        _createExternalContact: true,
      } as CreatePersonRequest}
    >
      <FormBlock>

        {!isReactNative() && (
          <FormRow>
            <SearchContactTextInput
              source="profile.name.firstName" label="First Name"
              searchLocalDatabase={false}
            />
            <SearchContactTextInput
              source="profile.name.lastName" label="Last Name"
              searchLocalDatabase={false}
            />
          </FormRow>
        )}

        {isReactNative() && (
          <FormRow>
            <TextInput
              source="profile.name.firstName"
              label="First Name"
              icon={InputLayoutIcon.PERSON} />
            <TextInput
              source="profile.name.lastName"
              label="Last Name"
              icon={InputLayoutIcon.PERSON}
            />
          </FormRow>
        )}

        <ExternalContactLinkWidget />

        <FormRow>
          <TextInput
            source="profile.primaryEmail"
            label="Primary Email"
            icon={InputLayoutIcon.EMAIL}
          />
          <TextInput
            source="profile.alternateEmail"
            label="Alternate Email"
            icon={InputLayoutIcon.EMAIL}
          />
        </FormRow>
        <FormRow>
          <TextInput
            source="profile.companyName"
            label="Company Name"
            icon={InputLayoutIcon.WORK}
          />
          <TextInput
            source="profile.website"
            label="Website"
            icon={InputLayoutIcon.WEB}
          />
        </FormRow>
        <FormRows>
          <PhoneNumbersInput source="profile.phoneNumbers" label="Phone Numbers" openCreateForm />
        </FormRows>
        <Separator />
        <AddressInput source="profile.address" />
        {!isReactNative() && (
          <>
            <Separator />
            <CheckboxInput
              source="profile.usePlainHtmlSignature"
              label="Use plain HTML for signature"
            />
            <IfFormData<CreatePersonRequest>
              conditionFunc={(data) => !!data.profile?.usePlainHtmlSignature}
            >
              <TextArea source="profile.emailSignature" label="Custom Email Signature" />
            </IfFormData>
            <IfFormData<CreatePersonRequest>
              conditionFunc={(data) => !data.profile?.usePlainHtmlSignature}
            >
              <Wysiwyg source="profile.emailSignature" label="Custom Email Signature" maxImageWidth={1200} />
            </IfFormData>
          </>
        )}
        {!isTeamMembersManagementEnabled && isGranted(TeamPermission.HAS_TEAM) && isGranted(TeamPermission.CREATE_TEAM_MEMBER) && (
          <>
            <Separator />
            <TeamRoleRadio source="teamRole" hideLabel />
            <IfFormData<CreatePersonRequest>
              conditionFunc={
                (data) => (!!data.teamRole && [
                  TeamRole.MEMBER,
                  TeamRole.ADMINISTRATOR,
                ].includes(data.teamRole))
              }
              onUnmount={(callback) => {
                callback({
                  projectsAssignment: undefined,
                });
              }}
            >
              <Separator />
              <RelativeRow weights={ifReactNative([12], [8, 4])}>
                <ProjectsMultiDropdown source="projectsAssignment.projectsIds"
                                       label="Transactions" />
                <IfFormData<CreatePersonRequest>
                  conditionFunc={
                    (data) => !!data.projectsAssignment?.projectsIds.length
                  }
                >
                  <ProjectRoleDropdown source="projectsAssignment.roleId" label="Role" />
                </IfFormData>
              </RelativeRow>
            </IfFormData>
          </>
        )}
        <IfFormData<CreatePersonRequest>
          conditionFunc={(data) => data.teamRole === TeamRole.CONTACT}
          onUnmount={(callback) => {
            callback({
              serviceProvidersIds: undefined,
            });
          }}
        >
          <Separator />
          <ServiceProvidersInput source="serviceProvidersIds" label="Service Providers" />
        </IfFormData>
        <FubIntegrationToggleWrapper>
          <AccessChecker permissionToCheck={TeamPermission.READ_FUB_CONNECTION}>
            <CreateExternalContactCheckbox />
          </AccessChecker>
        </FubIntegrationToggleWrapper>

      </FormBlock>
    </BasePersonFormTemplate>
  );
}

export function CreatePersonForm(
  props: {
    afterSubmitFunc?: AfterSubmitFunc<Person & {
      _externalContactMatches?: boolean,
      _externalContactCreationError?: string,
    }>,
    initialData?: CreatePersonRequest,
  }
) {

  return (
    <FubConnectionContext>
      <CreatePersonFormImpl {...props} />
    </FubConnectionContext>
  );
}
